import { registerActions } from "../actionRegistry";
/**
 * Collection management functions
 * Migrated from js.php
 */

import { showLoadingOverlay, toastSuccess, toastError } from "../ui/messages";
import { refreshModal, closeModalLast, modal } from "../ui/modal";
import { api } from "../api";
import { artworkCollectionsChanged } from "./collectionSignals";
import {
  getDomElement,
  getDomElementOrThrow,
  fadeOut,
  setText,
  setHtml,
  hide,
  queryAll,
  idSuffix,
} from "../utils/dom";
import { makeSortable } from "../plugins/sortable";

let reorderSelectedCount = 0;

/**
 * The `.lightbox-toggles` elements ARE the `#lb-state-<id>` inputs holding each
 * toggle's state, so the legacy `getDomElement("lb-state-" + lightbox_id)` lookup
 * resolved back to the element already in hand.
 */
function lightboxToggles(): { el: HTMLInputElement; id: string }[] {
  return Array.from(queryAll<HTMLInputElement>(".lightbox-toggles"), (el) => ({
    el,
    id: idSuffix(el, "lb-state-"),
  })).filter(({ id }) => id !== "");
}

/** Ids of the lightbox toggles currently switched on. */
function toggledLightboxIds(): string[] {
  return lightboxToggles()
    .filter(({ el }) => el.value === "1")
    .map(({ id }) => id);
}

/** Every toggle as the `"<id>:<state>"` CSV the save endpoints expect. */
function lightboxTogglesCsv(): string {
  return lightboxToggles()
    .map(({ el, id }) => id + ":" + el.value)
    .join(",");
}

/** Ids of the direct children of `parent`, stripped of a known prefix. */
function childIdSuffixes(parent: HTMLElement | null, prefix: string): string[] {
  if (!parent) return [];
  return Array.from(parent.children, (child) => idSuffix(child, prefix)).filter((s) => s !== "");
}

export async function deletefeaturedartist(id: string): Promise<void> {
  try {
    await api.artists.deleteFeatured({ userId: Number(id) });
    setTimeout(function () {
      refreshModal();
    }, 1000);
    toastSuccess("Featured artist deleted");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function deletecollection(id: string): Promise<void> {
  try {
    await api.collections.delete({ id });
    location.href = "/collections-workshop";
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function hidenotif(id: string): Promise<void> {
  try {
    await api.notifications.hide({ id: Number(id) });
    fadeOut("notification-" + id);
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function collectionfront(id: string): Promise<void> {
  try {
    await api.collections.setFrontpage({ id });
    toastSuccess("Collection featured!");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function linkcoll(coll_id: string, collcat_id: string): Promise<void> {
  try {
    await api.collections.linkColl({ collcatId: collcat_id, collId: coll_id });
    closeModalLast();
    modal("admin:collections:", "dynamic", 1);
    toastSuccess("Collection added to category");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function deletecollcatlink(collcat_id: string, coll_id: string): Promise<void> {
  if (!confirm("Are you sure you want to delete collection from category?")) return;

  try {
    await api.collections.deleteCatLink({ categoryId: collcat_id, collectionId: coll_id });
    modal("admin:collections:", "dynamic", 1);
    toastSuccess("Collection deleted from category");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function deletecollcat(collcat_id: string): Promise<void> {
  if (!confirm("Are you sure you want to delete entire collection category?")) return;

  try {
    await api.collectionCategories.delete({ collcatId: collcat_id });
    modal("admin:collections:", "dynamic", 1);
    toastSuccess("Collection category deleted");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function savecollectionorder(): Promise<void> {
  const save_order = childIdSuffixes(getDomElement("collection-target"), "img-")
    .map(Number)
    .filter((n) => n > 0);

  try {
    showLoadingOverlay("Saving collection order...");
    const collectionIdEl = getDomElementOrThrow<HTMLInputElement>("collection_id");
    await api.collections.saveCollectionOrder({
      id: collectionIdEl.value,
      order: save_order,
    });
    toastSuccess("Collection order saved");
    location.reload();
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export function enterReorderMode(): void {
  let reorder_selected = 0;

  for (const el of queryAll<HTMLElement>(".reorder-select")) {
    const raw_id = idSuffix(el, "reorder-select-");
    if (!raw_id) continue;
    if (getDomElement<HTMLInputElement>("reorder-select-input-" + raw_id)?.checked) {
      reorder_selected++;
    }
  }

  for (const el of queryAll<HTMLElement>(".reorder-move-here")) {
    el.classList.toggle("reorder-move-here-disabled", reorder_selected === 0);
  }

  reorderSelectedCount = reorder_selected;
}

export function docollmove(id: string): void {
  if (reorderSelectedCount <= 0) return;

  const agree = confirm(
    "Are you sure you want to move " + reorderSelectedCount + " artworks before " + id + "?"
  );
  if (!agree) return;

  const target = getDomElement("img-" + id);
  for (const el of queryAll<HTMLElement>(".reorder-select")) {
    const raw_id = idSuffix(el, "reorder-select-");
    if (!raw_id) continue;

    const checkbox = getDomElement<HTMLInputElement>("reorder-select-input-" + raw_id);
    if (!checkbox?.checked) continue;

    // The legacy chain was .remove().clone().insertBefore(target) — a move
    // expressed as detach + deep-clone + insert. Moving the node itself lands the
    // same markup; nothing binds per-node handlers (dispatch is delegated).
    const moving = getDomElement("img-" + raw_id);
    if (moving && target) target.before(moving);
    checkbox.checked = false;
  }
}

export async function saveordercollcat(): Promise<void> {
  const save_order = childIdSuffixes(getDomElement("collection_categories"), "collcat-");

  try {
    await api.collectionCategories.saveOrder({ order: save_order });
    toastSuccess("Category order saved");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function saveordercollcatlink(id: string): Promise<void> {
  const raw_id = id.replace("allcoll-", "");
  const save_order = childIdSuffixes(getDomElement(id), "link-" + raw_id + "-");

  try {
    await api.collectionCategories.saveLinkOrder({ id: raw_id, order: save_order });
    toastSuccess("Collection order saved");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export function collcatsort(id: string): void {
  makeSortable(id, {
    filter: "span.button",
    onEnd: function () {
      void saveordercollcatlink(id);
    },
  });
}

export async function deletecover(id: string): Promise<void> {
  try {
    await api.collections.deleteCover({ id });
    hide("coverupload_delete");
    setText("cover", "");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function fav(id: string): Promise<void> {
  try {
    const response = await api.lightbox.toggleFavorite({ id: Number(id) });
    const { state, fav: favId } = response;
    const isFav = state === 1;

    const favEl = getDomElement("fav-" + id);
    if (favEl) {
      favEl.classList.remove(isFav ? "red" : "grey");
      favEl.classList.add(isFav ? "grey" : "red");
      favEl.innerHTML = `<i class="fas fa-heart"></i> ${isFav ? "Favorited" : "Favorite"} <span class="key-shortcut"><i class="fas fa-keyboard"></i>F</span>`;
    }

    const lightboxIdEl = getDomElement<HTMLInputElement>("lightbox_id");
    const imgEl = getDomElement("img-" + id);
    if (lightboxIdEl?.value !== favId || !imgEl) return;

    imgEl.style.display = isFav ? "inline-block" : "none";
    const lbCountEl = getDomElement("lb-count");
    if (!lbCountEl) return;

    const newCount = parseInt(lbCountEl.innerHTML) + (isFav ? 1 : -1);
    setText("lb-count", String(newCount));
    setText("lb-label", newCount === 1 ? "artwork" : "artworks");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function briefcollectionsave(brief_id: string): Promise<void> {
  const savestr = toggledLightboxIds();

  if (savestr.length) {
    try {
      const response = await api.briefs.linkCollections({ id: brief_id, save: savestr });
      closeModalLast();
      toastSuccess("Collections added");
      const colls = response.collections;
      setHtml(
        "brief-collections-" + brief_id,
        Array.isArray(colls) ? colls.join(", ") : (colls ?? "")
      );
    } catch (err: unknown) {
      alert((err as Error).message);
    }
  }
}

export async function multiSaveToggledLightboxes(id: string): Promise<void> {
  const savestr = lightboxTogglesCsv();

  if (savestr !== "") {
    try {
      const response = await api.lightbox.addMultiPhotos({
        id: id
          .split(",")
          .map(Number)
          .filter((n) => n > 0),
        save: savestr,
      });
      closeModalLast();
      let the_message = "Added " + response.added_pic + " artwork";
      if (response.added_pic !== 1) {
        the_message += "s";
      }
      the_message += " into " + response.added_lb + " list";
      if (response.added_lb !== 1) {
        the_message += "s";
      }
      toastSuccess(the_message);
    } catch (err: unknown) {
      alert((err as Error).message);
    }
  }
}

export async function saveresellers(collection_id: string): Promise<void> {
  const savestr = toggledLightboxIds()
    .map(Number)
    .filter((n) => n > 0);

  if (savestr.length) {
    try {
      await api.collections.saveResellers({ id: collection_id, save: savestr });
      closeModalLast();
      toastSuccess("Saved re-sellers for collection");
    } catch (err: unknown) {
      alert((err as Error).message);
    }
  }
}

export async function savecollforresellers(collection_id: string): Promise<void> {
  const savestr = toggledLightboxIds()
    .map(Number)
    .filter((n) => n > 0);

  if (savestr.length) {
    try {
      await api.collections.saveCollForResellers({ id: collection_id, save: savestr });
      closeModalLast();
      toastSuccess("Saved re-sellers favorites for collection");
    } catch (err: unknown) {
      alert((err as Error).message);
    }
  }
}

export async function savecollection(id: string): Promise<void> {
  const nameEl = getDomElement<HTMLInputElement>("edit_collection_name");
  const urlEl = getDomElement<HTMLInputElement>("edit_collection_url");
  const subtitleEl = getDomElement<HTMLInputElement>("edit_collection_subtitle");
  const descEl = getDomElement<HTMLInputElement>("edit_collection_description");
  const relatedTagsEl = getDomElement<HTMLInputElement>("edit_collection_related_tags");
  const customhtmlEl = getDomElement<HTMLInputElement>("edit_collection_customhtml");
  const activeEl = getDomElement<HTMLInputElement>("edit_collection_active");
  const simpleLayoutEl = getDomElement<HTMLInputElement>("edit_collection_simple_layout");
  const colorEl = getDomElement<HTMLInputElement>("edit_collection_color_1");
  const modeEl = getDomElement<HTMLInputElement>("edit_mode_1");

  try {
    await api.collections.save({
      id: id,
      name: nameEl?.value ?? "",
      url: urlEl?.value ?? "",
      subtitle: subtitleEl?.value ?? "",
      description: descEl?.value ?? "",
      related_tags: relatedTagsEl?.value ?? "",
      customhtml: customhtmlEl?.value ?? "",
      active: activeEl?.checked ?? false,
      hide: simpleLayoutEl?.checked ? 1 : 0,
      color: colorEl?.checked ?? false,
      resellerSpecific: modeEl?.checked ?? false,
    });
    toastSuccess("Collection saved");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function saveToggledLightboxes(id: string): Promise<void> {
  const lightboxIdEl = getDomElement<HTMLInputElement>("lightbox_id");
  const lightbox_id_current = lightboxIdEl?.value ?? "";
  const savestr = lightboxTogglesCsv();

  if (savestr !== "") {
    try {
      const response = await api.lightbox.addPhoto({ id: Number(id), save: savestr });
      // Same signal as the collection picker: a list is a non-PUBLIC Collection, so
      // "this artwork's Collection membership moved" covers both. Subscribers re-read the
      // counts rather than guessing which kind changed.
      artworkCollectionsChanged(Number(id));
      closeModalLast();
      const the_message =
        response.lightboxstate === 1
          ? "List updated"
          : response.lightboxstate === 2
            ? "List updated"
            : "List saved";
      toastSuccess(the_message);

      // Update lightbox count if applicable
      if (lightbox_id_current) {
        const stateEl = getDomElement<HTMLInputElement>("lb-state-" + lightbox_id_current);
        if (stateEl?.value === "0") {
          hide("img-" + id);
          const lbCountEl = getDomElement("lb-count");
          if (lbCountEl) {
            const newCount = parseInt(lbCountEl.innerHTML) - 1;
            setText("lb-count", String(newCount));
            if (newCount === 1) {
              setText("lb-label", "artwork");
            } else {
              setText("lb-label", "artworks");
            }
          }
        }
      }
    } catch (err: unknown) {
      alert((err as Error).message);
    }
  }
}

export async function saveToggledCollections(id: string): Promise<void> {
  const savestr = lightboxTogglesCsv();

  if (savestr !== "") {
    try {
      const response = await api.collections.addPhoto({ id: Number(id), save: savestr });
      // Tell whoever opened this modal that the artwork's membership moved. It closes
      // itself on save, so without this the opener has no way to learn the outcome — see
      // features/collectionSignals.ts.
      artworkCollectionsChanged(Number(id));
      closeModalLast();
      const the_message =
        response.lightboxstate === 1
          ? "Collection updated"
          : response.lightboxstate === 2
            ? "Collection updated"
            : "Collection saved";
      toastSuccess(the_message);
    } catch (err: unknown) {
      alert((err as Error).message);
    }
  }
}

export async function multiSaveToggledCollections(id: string): Promise<void> {
  const savestr = lightboxTogglesCsv();

  if (savestr !== "") {
    try {
      const response = await api.collections.addMultiPhotos({ id, save: savestr });
      closeModalLast();
      let the_message = "Added " + response.added_pic + " artwork";
      if (response.added_pic !== 1) {
        the_message += "s";
      }
      the_message += " into " + response.added_lb + " collection";
      if (response.added_lb !== 1) {
        the_message += "s";
      }
      toastSuccess(the_message);
    } catch (err: unknown) {
      alert((err as Error).message);
    }
  }
}

export async function savefeaturedartistsorder(): Promise<void> {
  const save_order = childIdSuffixes(getDomElement("featured-artists"), "fa-")
    .map(Number)
    .filter((n) => n > 0);

  try {
    await api.artists.saveFeaturedOrder({ order: save_order });
    toastSuccess("Featured artists order saved");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function savecollcat(id: string): Promise<void> {
  const nameEl = getDomElement<HTMLInputElement>("collcat_name_" + id);
  const urlEl = getDomElement<HTMLInputElement>("collcat_url_" + id);

  try {
    await api.collectionCategories.save({
      id,
      name: nameEl?.value ?? "",
      url: urlEl?.value ?? "",
    });
    toastSuccess("Category saved");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export function coverdone(): void {
  // Only reached on success — cover.uploadComplete throws on failure and the
  // presigned pipeline surfaces the error itself. It used to take
  // `(filename, response)` and ignore both, a leftover of the PHP-era Dropzone
  // callback signature.
  toastSuccess("Cover uploaded");
}

export async function savelove(): Promise<void> {
  const idEl = getDomElement<HTMLInputElement>("artwork_id");
  if (!idEl?.value) return;

  try {
    await api.collections.saveLove({ id: idEl.value });
    toastSuccess("Loved!");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

/**
 * Set a collection's THUMB (`display_artwork_id`), not its cover.
 *
 * `pic_id` is typed `number` deliberately: it used to be `string` with a `Number()` at the
 * call, which turned a mistakenly-passed image hash into NaN and surfaced the server's
 * validation error verbatim. The type now rejects that at compile time.
 */
export async function setcollthumb(pic_id: number, collection_id: string): Promise<void> {
  try {
    await api.collections.setThumb({ collId: collection_id, picId: pic_id });
    closeModalLast();
    toastSuccess("Collection thumb set");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

registerActions({
  hidenotif,
  reorderselect: enterReorderMode,
  saveordercollcatlink,
  savelove,
});
